import React, { useState, useEffect } from 'react';
import dataManager from '../utils/dataManager';
import { useAuth } from '../contexts/AuthContext';
import UserMenu from './UserMenu';
import AuthModal from './AuthModal';

const Sidebar = ({ activeView, setActiveView, isSidebarOpen, setIsSidebarOpen }) => {
  const { user } = useAuth();
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState('login');
  const [philosophies, setPhilosophies] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigationItems = [
    {
      id: 'dashboard',
      name: '体式社区',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
        </svg>
      )
    },
    {
      id: 'community-stories',
      name: '社区故事',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
      )
    },
    {
      id: 'feeling-insights',
      name: '感受洞察',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      )
    },

    {
      id: 'add-reflection',
      name: '添加体式反思',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
        </svg>
      )
    },
    {
      id: 'teacher-list',
      name: '导师团队',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
        </svg>
      )
    },
    {
      id: 'exercise-list',
      name: '健身动作库',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 9v6m0-6a2 2 0 00-2 2v2a2 2 0 002 2m0-6v6m16-6v6m0-6a2 2 0 012 2v2a2 2 0 01-2 2m0-6v6M4 12h16" />
        </svg>
      )
    },
    {
      id: 'my-posts',
      name: '我的帖子',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
        </svg>
      )
    },

  ];

  // 加载哲学数据
  useEffect(() => {
    const loadPhilosophies = async () => {
      try {
        setLoading(true);
        const philosophiesData = await dataManager.getPracticePhilosophy();
        setPhilosophies(Array.isArray(philosophiesData) ? philosophiesData : []);
      } catch (err) {
        console.error('Error loading philosophies in sidebar:', err);
        // 降级到本地数据
        const fallbackData = dataManager.getPracticePhilosophySync() || [];
        setPhilosophies(fallbackData);
      } finally {
        setLoading(false);
      }
    };

    loadPhilosophies();
  }, []);

  const handleMenuClick = (view) => {
    // 检查是否需要登录才能访问
    const protectedViews = ['add-reflection']; // 只有添加体式反思需要登录

    if (protectedViews.includes(view) && !user) {
      setAuthModalMode('login');
      setAuthModalOpen(true);
      return;
    }

    setActiveView(view);
    // 在移动设备上点击菜单项后关闭侧边栏
    if (setIsSidebarOpen) {
      setIsSidebarOpen(false);
    }
  };

  const handleAuthClick = (mode) => {
    setAuthModalMode(mode);
    setAuthModalOpen(true);
  };

  return (
    <aside className={`
      ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      fixed lg:static inset-y-0 left-0 z-50 w-72 max-w-[82vw] h-[100dvh] max-h-[100dvh] bg-white/85 lg:bg-white/30 backdrop-blur-md border-r border-sage-200/50 flex flex-col overflow-hidden
      transition-transform duration-300 ease-in-out
    `}>
      {/* Header */}
      <div className="shrink-0 border-b border-sage-200/50 p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="truncate text-2xl font-serif font-bold leading-tight text-sage-800">
              Ashtanga Flow
            </h1>
            <p className="text-sm text-sage-600 mt-1">体式反思与交流</p>
          </div>

          <button
            type="button"
            aria-label="关闭菜单"
            onClick={() => setIsSidebarOpen?.(false)}
            className="lg:hidden flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-sage-200 bg-white/80 text-sage-700 transition-colors hover:bg-sage-50 active:bg-sage-100"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* 用户认证区域 */}
        <div className="mt-4">
          {user ? (
            <div className="rounded-xl border border-sage-100 bg-white/60 p-1">
              <UserMenu />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => handleAuthClick('login')}
                className="h-10 rounded-xl bg-sage-600 px-3 text-sm font-medium text-white transition-colors hover:bg-sage-700 active:bg-sage-800"
              >
                登录
              </button>
              <button
                type="button"
                onClick={() => handleAuthClick('register')}
                className="h-10 rounded-xl border border-sage-300 bg-white/70 px-3 text-sm font-medium text-sage-700 transition-colors hover:bg-sage-50 active:bg-sage-100"
              >
                注册
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="sidebar-scroll flex-1 min-h-0 overflow-y-auto overscroll-contain p-4">
        <div className="mb-6">
          <h2 className="text-xs font-semibold text-sage-500 uppercase tracking-wider mb-3">
            NAVIGATION
          </h2>
          <div className="space-y-1">
            {navigationItems.map((item) => (
              <div
                key={item.id}
                onClick={() => handleMenuClick(item.id)}
                className={`sidebar-item group ${activeView === item.id ? 'active' : ''}`}
              >
                <span className="mr-3 group-hover:scale-110 transition-transform duration-300">
                  {item.icon}
                </span>
                <span className="text-sm font-medium">{item.name}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-6">
          <h2 className="text-xs font-semibold text-sage-500 uppercase tracking-wider mb-3">
            PRACTICE PHILOSOPHY
          </h2>
          <div className="space-y-3">
            {loading ? (
              <div className="text-center py-4">
                <div className="text-sage-400">加载中...</div>
              </div>
            ) : philosophies.length === 0 ? (
              <div className="text-center py-4">
                <div className="text-sage-400">暂无哲学原则</div>
              </div>
            ) : (
              philosophies.map((item, index) => (
              <div
                key={index}
                className="bg-sage-50/50 rounded-lg p-3 hover:bg-sage-100/50 transition-colors duration-200 cursor-pointer"
                onClick={() => handleMenuClick(`philosophy-${item.name.toLowerCase().replace(/\s+/g, '-')}`)}
              >
                <div className="flex items-center mb-2">
                  <span className="mr-3 text-lg floating-animation" style={{ animationDelay: `${index * 0.5}s` }}>
                    {item.icon}
                  </span>
                  <span className="text-sm font-medium text-sage-700">{item.name}</span>
                </div>
                <p className="text-xs text-sage-600 leading-relaxed ml-8">
                  {item.description}
                </p>
                <div className="mt-2 ml-8">
                  <span className="text-xs text-sage-500 hover:text-sage-700 transition-colors">
                    点击了解更多 →
                  </span>
                </div>
              </div>
              ))
            )}
          </div>
        </div>
      </nav>

      {/* Footer */}
      <div className="shrink-0 p-4 border-t border-sage-200/50">
        <div className="flex items-center text-sage-600 text-sm">
          <div className="w-2 h-2 bg-sage-400 rounded-full mr-2 animate-pulse"></div>
          <span>Namaste 🙏</span>
        </div>
        <p className="text-xs text-sage-500 mt-1">Share your practice journey</p>
      </div>

      {/* 认证模态框 */}
      <AuthModal
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        initialMode={authModalMode}
      />
    </aside>
  );
};

export default Sidebar; 
